/** Must match config/media.php duration.match_tolerance_ms and VideoDurationTarget. */
export const DURATION_MATCH_TOLERANCE_MS = 500;

/** Must match config/media.php duration.min_ms / max_ms. */
export const DURATION_MIN_MS = 1000;
export const DURATION_MAX_MS = 60000;

export const PICTURE_DURATION_MS = 5000;

export const MEDIA_KIND_VIDEO = 'video';
export const MEDIA_KIND_PICTURE = 'picture';

/**
 * @param {File|null|undefined} file
 * @returns {'video'|'picture'|null}
 */
export const mediaKindFromFile = (file) => {
    const type = String(file?.type || '');
    if (type.startsWith('video/')) return MEDIA_KIND_VIDEO;
    if (type.startsWith('image/')) return MEDIA_KIND_PICTURE;
    return null;
};

/**
 * @returns {{ kind: 'video'|'picture'|null, width: number|null, height: number|null, duration: number|null }}
 */
export const emptyMediaMeta = () => ({
    kind: null,
    width: null,
    height: null,
    duration: null,
});

/**
 * @param {{ width?: number|null, height?: number|null }|null|undefined} meta
 */
export const hasDimensions = (meta) => {
    const width = Number(meta?.width);
    const height = Number(meta?.height);
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
};

/**
 * @param {{ duration?: number|null }|null|undefined} meta
 */
export const hasDuration = (meta) => {
    const duration = Number(meta?.duration);
    return Number.isFinite(duration) && duration > 0;
};

/**
 * @param {{ width?: number|null, height?: number|null }} meta
 */
export const formatDimensions = (meta) => `${Number(meta.width)}×${Number(meta.height)}`;

/**
 * @param {{ duration?: number|null }|number} metaOrMs
 */
export const durationMsValue = (metaOrMs) =>
    typeof metaOrMs === 'number' ? metaOrMs : Number(metaOrMs?.duration);

/**
 * @param {{ duration?: number|null }|number} metaOrMs
 */
export const formatDuration = (metaOrMs) => `${Math.round(durationMsValue(metaOrMs) / 1000)} сек`;

/**
 * @param {{ duration?: number|null }|number} metaOrMs
 */
export const formatDurationMs = (metaOrMs) => `${Math.round(durationMsValue(metaOrMs))} мс`;

/**
 * @param {{ width?: number|null, height?: number|null, duration?: number|null }|null|undefined} meta
 * @param {{ includeDuration?: boolean }} [options]
 */
export const formatMediaSpecsLine = (meta, options = {}) => {
    const includeDuration = options.includeDuration !== false;
    const parts = [];
    if (hasDimensions(meta)) parts.push(formatDimensions(meta));
    if (includeDuration && hasDuration(meta)) parts.push(formatDuration(meta));
    return parts.join(' · ');
};

/**
 * @param {{ width?: number|null, height?: number|null }} a
 * @param {{ width?: number|null, height?: number|null }} b
 */
export const dimensionsMatch = (a, b) =>
    Number(a.width) === Number(b.width) && Number(a.height) === Number(b.height);

/**
 * @param {{ duration?: number|null }} a
 * @param {{ duration?: number|null }} b
 * @param {number} [toleranceMs]
 */
export const durationsMatch = (a, b, toleranceMs = DURATION_MATCH_TOLERANCE_MS) =>
    Math.abs(Number(a.duration) - Number(b.duration)) <= toleranceMs;

/**
 * Must match App\Services\Media\VideoDurationTarget::nearestSecondMs (half-up).
 *
 * @param {number} durationMs
 * @returns {number}
 */
export const nearestDurationMs = (durationMs) => Math.round(Number(durationMs) / 1000) * 1000;

/**
 * @param {{ duration?: number|null }|number} a
 * @param {{ duration?: number|null }|number} b
 */
export const durationsRoundToSameSecond = (a, b) =>
    nearestDurationMs(durationMsValue(a)) === nearestDurationMs(durationMsValue(b));

/**
 * @param {number} originalMs
 * @param {number} [targetMs]
 * @returns {string}
 */
export const formatDurationNormalizeHint = (originalMs, targetMs = nearestDurationMs(originalMs)) => {
    const original = Number(originalMs);
    const target = Number(targetMs);
    if (!Number.isFinite(original) || original <= 0 || !Number.isFinite(target) || target <= 0) {
        return '';
    }
    if (original === target) return '';
    return `${original} мс → ${target} мс`;
};

/**
 * Reads width/height from the file. Duration is in milliseconds for video; null for images.
 *
 * @param {File} file
 * @returns {Promise<{ kind: 'video'|'picture', width: number, height: number, duration: number|null }>}
 */
export const readMediaMeta = (file) => {
    if (!file) {
        return Promise.reject(new Error('Файл не выбран'));
    }

    const kind = mediaKindFromFile(file);
    if (!kind) {
        return Promise.reject(new Error('Нужен файл изображения или видео'));
    }

    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const cleanup = () => URL.revokeObjectURL(objectUrl);

        if (kind === MEDIA_KIND_VIDEO) {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                const width = video.videoWidth;
                const height = video.videoHeight;
                const duration = Math.floor(video.duration * 1000);
                cleanup();
                if (!width || !height) {
                    reject(new Error('Не удалось прочитать размеры видео'));
                    return;
                }
                resolve({ kind, width, height, duration });
            };
            video.onerror = () => {
                cleanup();
                reject(new Error('Не удалось прочитать видео'));
            };
            video.src = objectUrl;
            return;
        }

        const image = new Image();
        image.onload = () => {
            const width = image.width;
            const height = image.height;
            cleanup();
            if (!width || !height) {
                reject(new Error('Не удалось прочитать размеры изображения'));
                return;
            }
            resolve({ kind, width, height, duration: null });
        };
        image.onerror = () => {
            cleanup();
            reject(new Error('Не удалось прочитать изображение'));
        };
        image.src = objectUrl;
    });
};
